/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { theme } from "styles/theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { TDevice, THomeSetting, getHomeSettingInfo, updateHomeSettingInfo } from "apis/visitors/device";
import { useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";
import { ChromePicker } from "react-color";
import IOSSwitch from "uis/iosSwitch";
import { TabBtn, TabContainer } from "pages/visitors/visit-guide-manage/uis";
import TextArea from "../components/text-area";
import Preview from "../preview/thema-preview";
import Button from "uis/Button";
import { LoadingModal } from "uis/modal";
import Tab from "../tab";

const ColorContainer = styled.div<{ backgroundColor: string }>`
  background-color: ${(props) => props.backgroundColor};
  width: 80px;
  height: 40px;
  border-radius: 10px;
  align-items: center;
  display: flex;
  justify-content: center;
  border: 1px solid ${theme.neutral100};
  cursor: pointer;
`;

const ColorLabel = styled.p<{ color: string }>`
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => props.color};
`;

const ColorSelector = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  position: relative;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.neutral100};
  margin-top: 12px;
`;

const Title = styled.p`
  font-size: 16px;
  font-weight: 700;
`;

const SectionTitle = styled.p`
  color: ${theme.neutral500};
  font-size: 17px;
  font-weight: 600;
`;

const RootContainer = styled.div`
  display: flex;
  padding: 35px 40px;
  flex-direction: column;
  width: 1000px;
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 500px;
`;

const ActivateContainer = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 24px;
  background-color: ${theme.neutral50};
  margin: 12px 0;
`;

export const getTextColorByBackgroundColor = (hexColor: string) => {
  const c = hexColor.substring(1);
  const rgb = parseInt(c, 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = (rgb >> 0) & 0xff;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luma < 127.5 ? "white" : "black";
};

const HomeTheme = ({ device, onChange }: { device: TDevice; onChange?: () => void }) => {
  const [primary, setPrimary] = useState<string>(device.visitorsSettingTheme.primary);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenPrimary, setIsOpenPrimary] = useState<boolean>(false);
  const primaryRef = useRef<HTMLObjectElement>(null);
  const workspaceInfo = useRecoilValue(workspace);
  const [homeSetting, setHomeSetting] = useState<THomeSetting>();
  const [previewLang, setPreviewLang] = useState<"kor" | "eng">("kor");
  const defaultKorGreeting = `안녕하세요.<br/><strong>${workspaceInfo?.name}</strong>입니다.<br/>방문을 환영합니다. 😀`;
  const defaultEngGreeting = `Hello.<br/>We are <strong>${workspaceInfo?.name}</strong>.<br/>Thank you for visiting. 😀`;
  const defaultKorInfoMessage = "태블릿을 통해 담당자를 호출해주세요.";
  const defaultEngInfoMessage = "Please call the person in charge through the tablet.";

  const closeColor = useCallback((e: any) => {
    if (primaryRef.current && !primaryRef.current.contains(e.target)) {
      setIsOpenPrimary(false);
    }
  }, []);

  const onChangeGreetingKor = (value: string) => {
    if (homeSetting) setHomeSetting({ ...homeSetting, greetingInKorFormat: value });
  };

  const onChangeGreetingEng = (value: string) => {
    if (homeSetting) setHomeSetting({ ...homeSetting, greetingInEngFormat: value });
  };

  const onChangeGreetingActive = (value: boolean) => {
    if (homeSetting && value === false) setHomeSetting({ ...homeSetting, isGreetingDefault: value });
    if (homeSetting && value === true)
      setHomeSetting({
        ...homeSetting,
        isGreetingDefault: value,
        greetingInKorFormat: defaultKorGreeting,
        greetingInEngFormat: defaultEngGreeting,
      });
  };

  const onChangeInfoMessageActive = (value: boolean) => {
    if (homeSetting && value === false) {
      setHomeSetting({
        ...homeSetting,
        isInfoMessageEnabled: value,
        infoMessageInKorFormat: defaultKorInfoMessage,
        infoMessageInEngFormat: defaultEngInfoMessage,
      });
    }
    if (homeSetting && value) {
      setHomeSetting({
        ...homeSetting,
        isInfoMessageEnabled: value,
      });
    }
  };

  const onChangeInfoMessageKor = (value: string) => {
    if (homeSetting) {
      setHomeSetting({ ...homeSetting, infoMessageInKorFormat: value });
    }
  };

  const onChangeInfoMessageEng = (value: string) => {
    if (homeSetting) {
      setHomeSetting({ ...homeSetting, infoMessageInEngFormat: value });
    }
  };

  const onSaveHomeSetting = async () => {
    setLoading(true);
    if (homeSetting) {
      const res = await updateHomeSettingInfo({
        ...homeSetting,
        visitorsSettingTheme: { id: "", primary: primary },
      });
      if (res) {
        onChange?.();
      } else alert("잠시후 다시 시도해주세요.");
      setLoading(false);
    }
  };

  window.addEventListener("click", (e) => {
    window.addEventListener("click", closeColor);
  });

  useEffect(() => {
    getHomeSettingInfo(device.id).then((res) => {
      setHomeSetting(res);
    });
  }, []);

  return (
    <div
      css={css`
        overflow: auto;
        height: 80%;
      `}
    >
      <RootContainer>
        <Button
          onClick={onSaveHomeSetting}
          title="저장하기"
          styles={{
            container: css`
              width: 140px;
              padding: 12px 0;
              margin-left: auto;
            `,
          }}
        />
        <div
          css={css`
            display: flex;
            gap: 60px;
          `}
        >
          <LeftContainer>
            {/* 색상 설정 */}
            <SectionTitle>색상 설정</SectionTitle>
            <ColorSelector ref={primaryRef}>
              <Title>Primary</Title>
              <ColorContainer backgroundColor={primary} onClick={() => setIsOpenPrimary(true)}>
                <ColorLabel color={getTextColorByBackgroundColor(primary)}>{primary}</ColorLabel>
              </ColorContainer>
              {isOpenPrimary ? (
                <ChromePicker
                  color={primary}
                  onChange={(color) => {
                    setPrimary(color.hex);
                  }}
                  css={css`
                    position: absolute;
                    top: 52px;
                    right: 0%;
                    z-index: 1;
                  `}
                />
              ) : null}
            </ColorSelector>

            {/* 인삿말 설정 */}
            <SectionTitle
              css={css`
                margin-top: 40px;
              `}
            >
              인삿말 설정
            </SectionTitle>
            <GreetingTextArea
              homeSetting={homeSetting}
              onChangeKor={onChangeGreetingKor}
              onChangeEng={onChangeGreetingEng}
              onChangeActive={onChangeGreetingActive}
            />

            {/* 안내메세지 설정 */}
            <SectionTitle
              css={css`
                margin-top: 100px;
              `}
            >
              안내 메세지 설정
            </SectionTitle>
            <InfoTextArea
              homeSetting={homeSetting}
              onChangeKor={onChangeInfoMessageKor}
              onChangeEng={onChangeInfoMessageEng}
              onChangeActive={onChangeInfoMessageActive}
            />
          </LeftContainer>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 68px;
            `}
          >
            {homeSetting && workspaceInfo && (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 24px;
                `}
              >
                <Tab
                  selected={previewLang === "eng" ? "영어" : "한국어"}
                  data={["한국어", "영어"]}
                  onChange={(value) => {
                    if (value === "한국어") {
                      setPreviewLang("kor");
                    } else {
                      setPreviewLang("eng");
                    }
                  }}
                />
                <Preview settingInfo={homeSetting} workspace={workspaceInfo} primary={primary} lang={previewLang} />
              </div>
            )}
          </div>
          <LoadingModal modalIsOpen={loading} />
        </div>
      </RootContainer>
    </div>
  );
};

export default HomeTheme;

const GreetingTextArea = ({
  homeSetting,
  onChangeKor,
  onChangeEng,
  onChangeActive,
}: {
  homeSetting?: THomeSetting;
  onChangeKor?: (value: string) => void;
  onChangeEng?: (value: string) => void;
  onChangeActive?: (value: boolean) => void;
}) => {
  const [lang, setLang] = useState<"한국어" | "영어">("한국어");
  return (
    <>
      <ActivateContainer>
        <p>인삿말 커스터마이징 활성화</p>
        <IOSSwitch
          checked={!homeSetting?.isGreetingDefault}
          css={css`
            margin-left: auto;
          `}
          onChange={(e, checked) => onChangeActive?.(!checked)}
        />
      </ActivateContainer>
      {!homeSetting?.isGreetingDefault && (
        <>
          <TabContainer>
            <TabBtn isPressed={lang === "한국어"} onClick={() => setLang("한국어")}>
              한국어
            </TabBtn>
            <TabBtn isPressed={lang === "영어"} onClick={() => setLang("영어")}>
              영어
            </TabBtn>
          </TabContainer>
          {lang === "한국어" && <TextArea value={homeSetting?.greetingInKorFormat} onChange={onChangeKor} />}
          {lang === "영어" && <TextArea value={homeSetting?.greetingInEngFormat} onChange={onChangeEng} />}
        </>
      )}
    </>
  );
};

const InfoTextArea = ({
  homeSetting,
  onChangeActive,
  onChangeKor,
  onChangeEng,
}: {
  homeSetting?: THomeSetting;
  onChangeActive?: (value: boolean) => void;
  onChangeKor: (value: string) => void;
  onChangeEng: (value: string) => void;
}) => {
  const [lang, setLang] = useState<"한국어" | "영어">("한국어");
  return (
    <>
      <ActivateContainer>
        <p>안내 메세지 활성화</p>
        <IOSSwitch
          onChange={(e, checked) => {
            onChangeActive?.(checked);
          }}
          checked={homeSetting?.isInfoMessageEnabled}
          css={css`
            margin-left: auto;
          `}
        />
      </ActivateContainer>
      {homeSetting?.isInfoMessageEnabled && (
        <>
          <TabContainer>
            <TabBtn isPressed={lang === "한국어"} onClick={() => setLang("한국어")}>
              한국어
            </TabBtn>
            <TabBtn isPressed={lang === "영어"} onClick={() => setLang("영어")}>
              영어
            </TabBtn>
          </TabContainer>
          {lang === "한국어" && <TextArea value={homeSetting?.infoMessageInKorFormat} onChange={onChangeKor} />}
          {lang === "영어" && <TextArea value={homeSetting?.infoMessageInEngFormat} onChange={onChangeEng} />}
        </>
      )}
    </>
  );
};
